import {
  ComponentType,
} from 'react';
import {
  DrawerClasses,
} from '@mui/material/Drawer';
import isNil from 'lodash/isNil';
import {
  ActivePanel,
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';

export const PANEL_PARAM = 'panel';

// Page params synced to the URL. Extend when a page syncs a new one, so no
// panel can declare it.
const RESERVED_PARAMS = [PANEL_PARAM, 'search', 'dates', 'date'];

export type PanelDefinition<P> = {
  Component: ComponentType<SidePanelComponentProps<P>>;
  classes?: Partial<DrawerClasses>;
  getPanelIdentity: (panelInfo: P) => string;
  // Names the record in the "not available" panel, such as "to-do"
  getRecordName?: (panelInfo: P) => string;
  url?: {
    params: readonly string[];
    serialize: (panelInfo: P) => Record<string, string>;
    parse: (searchParams: URLSearchParams) => P | null;
    // Instances it returns false for stay in memory. Missing: all are linkable
    isLinkable?: (panelInfo: P) => boolean;
  };
};

export type PanelRegistry = Record<string, PanelDefinition<any>>;

export const POSITIVE_INTEGER = /^[1-9]\d*$/;

/** URL part for a panel found by one numeric id, such as `todoId`. */
export function idParamUrl<K extends string>(param: K): NonNullable<PanelDefinition<Record<K, number>>['url']> {
  return {
    params: [param],
    serialize: (panelInfo) => ({ [param]: String(panelInfo[param]) }) as Record<string, string>,
    parse: (searchParams) => {
      const raw = searchParams.get(param);
      return !isNil(raw) && POSITIVE_INTEGER.test(raw)
        ? { [param]: Number(raw) } as Record<K, number>
        : null;
    },
  };
}

export function getDefinition(registry: PanelRegistry, type: string) {
  return Object.hasOwn(registry, type) ? registry[type] : null;
}

/** Returns the URL part that links this panel instance, or null if it is not linkable. */
export function getPanelUrl(registry: PanelRegistry, type: string, panelInfo: unknown) {
  const url = getDefinition(registry, type)?.url;

  if (isNil(url) || !(url.isLinkable?.(panelInfo) ?? true)) {
    return null;
  }

  return url;
}

/** Returns the registry, or throws if a panel declares a reserved param. */
export function definePanelRegistry<R extends PanelRegistry>(registry: R): R {
  Object.entries(registry).forEach(([type, definition]) => {
    const reserved = definition.url?.params.find((param) => RESERVED_PARAMS.includes(param));

    if (!isNil(reserved)) {
      throw new Error(`Side panel "${type}" declares the reserved param "${reserved}"`);
    }
  });

  return registry;
}

export function parsePanelParams(
  searchParams: URLSearchParams,
  registry: PanelRegistry,
): ActivePanel | null {
  const type = searchParams.get(PANEL_PARAM);

  if (isNil(type)) {
    return null;
  }

  const url = getDefinition(registry, type)?.url;
  const panelInfo = url?.parse(searchParams);

  if (isNil(panelInfo) || isNil(getPanelUrl(registry, type, panelInfo))) {
    return null;
  }

  return {
    type,
    panelInfo,
    entityKey: registry[type].getPanelIdentity(panelInfo),
  };
}

/** Deletes `panel` and every param declared by a panel in the registry. */
export function clearPanelParams(
  searchParams: URLSearchParams,
  registry: PanelRegistry,
) {
  searchParams.delete(PANEL_PARAM);
  Object.values(registry).forEach((definition) => {
    definition.url?.params.forEach((param) => {
      searchParams.delete(param);
    });
  });
}

/**
 * Returns only `panel` and the declared panel params, sorted, so two URLs can
 * be compared by the panel they hold.
 */
export function getPanelSearch(
  searchParams: URLSearchParams,
  registry: PanelRegistry,
) {
  const declared = new Set([
    PANEL_PARAM,
    ...Object.values(registry).flatMap((definition) => definition.url?.params ?? []),
  ]);
  const panelParams = new URLSearchParams();

  searchParams.forEach((value, param) => {
    if (declared.has(param)) {
      panelParams.append(param, value);
    }
  });
  panelParams.sort();

  return panelParams.toString();
}

/**
 * Replaces the panel in the URL with the given one. Throws, before changing
 * anything, for a non-linkable panel or an undeclared serialized param.
 */
export function setPanelParams(
  searchParams: URLSearchParams,
  type: string,
  panelInfo: unknown,
  registry: PanelRegistry,
) {
  const url = getPanelUrl(registry, type, panelInfo);

  if (isNil(url)) {
    throw new Error(`Side panel "${type}" is not linkable`);
  }

  const values = url.serialize(panelInfo);
  const undeclared = Object.keys(values).find((param) => !url.params.includes(param));

  if (!isNil(undeclared)) {
    throw new Error(`Side panel "${type}" serialized the undeclared param "${undeclared}"`);
  }

  clearPanelParams(searchParams, registry);
  searchParams.set(PANEL_PARAM, type);
  Object.entries(values).forEach(([param, value]) => {
    searchParams.set(param, value);
  });
}
